<script setup>
import { computed, inject } from 'vue'
import SubscriptionService from '@/services/SubscriptionService'
import { useStore } from 'vuex'
import _ from 'lodash'
import FormButton from '../../components/Atoms/FormButton.vue'
import Tooltip from '../Atoms/Tooltip.vue'
import Badge from '../../components/Badge.vue'
import { openCalendly } from '@/utils'
import { track, EVENTS } from '@/lib/tracking'

const props = defineProps({
  plan: {
    type: Object,
  },
  isYearly: {
    type: Boolean
  },
  isCart: {
    type: Boolean
  },
  productType: {
    type: String
  }
})

const store = useStore()

const { updateSelectedPaymentMethod, selectPackage } = inject('selectedPaymentMethod')

// Computed
const addonSubscriptions = computed(() => store.getters['User/addonSubscriptions'])

const price = computed(() => props.isYearly ? props.plan.info?.yearly : props.plan.baseCharge)

const isEventsPlan = computed(() => props.plan?.info?.name === 'Events')
const reserveBadgeRow = computed(() => !props.isCart && props.productType !== 'ott')

const labelComputed = computed(() => {
  if (props.plan?.name === 'Custom') return 'Contact Us'
  return isCurrentSubscription.value ? 'Active plan' : 'Subscribe Now'
})

const shouldShowBookMeetingButton = (plan) => {
  if (plan?.info?.name === 'Events') return true
  if (plan?.info?.name === 'Ultra') return true
  return false
}
const isCurrentSubscription = computed(() => {
  const subscription = props.productType === 'ott'
    ? addonSubscriptions.value.find(subscription => subscription.category === 'ott' && subscription.enabled)
    : addonSubscriptions.value.find(subscription => subscription.category === 'live' && subscription.enabled)
  return (subscription?.package?._id === props.plan?._id) && props.plan.name !== 'Custom'
})

const isDisabled = computed(() => isCurrentSubscription.value)
// Methods
const getChangeDirection = (pack) => {
  const currentPackage = store.getters['User/baseSubscription']?.package
  if (!currentPackage) return 'new'
  const toMonthly = (p) => (p?.cycleAge === 365 ? (p?.baseCharge || 0) / 12 : (p?.baseCharge || 0))
  const next = toMonthly(pack)
  const current = toMonthly(currentPackage)
  if (next > current) return 'upgrade'
  if (next < current) return 'downgrade'
  return 'same'
}

const selectSubscriptionPackage = async (pack) => {
  selectPackage(pack)
  const checkpaymentMethods = await SubscriptionService.getUserPaymentMethods()
  updateSelectedPaymentMethod(checkpaymentMethods[0])
  track(EVENTS.PLAN_SELECTED, {
    packageId: pack._id,
    planName: pack.info?.name || pack.name,
    planCategory: pack.category,
    billingCycle: pack.cycleAge === 365 ? 'yearly' : 'monthly',
    baseCharge: pack.baseCharge,
    changeDirection: getChangeDirection(pack),
  })
}
</script>

<template>
  <!-- A floor, not a fixed height: the header is a grid item sharing a row with
       the other plans, so the tallest content sizes the row and every column
       stretches to it. A fixed height let a narrow column's content overflow,
       and flex then shrank the empty badge row and the billing lines — which is
       how the Events price drifted 4px below its neighbours at 1280.
       `container-type` lets the price and ribbon adapt to the column's own
       width (see <style>). -->
  <header class="!px-4 !py-5 flex flex-col relative bg-surface-2 rounded-t-xl overflow-hidden [container-type:inline-size]"
    :class="[isCart ? '' : (productType === 'ott' ? 'min-h-[120px]' : 'min-h-[256px] lg:min-h-[268px]')]">
    <h6 v-if="isEventsPlan" class="plan-ribbon text-white text-xs leading-4 bg-main py-1 absolute top-0 right-0 w-full max-w-full text-center">Branded Membership Site</h6>
    <h2 class="text-lg font-bold !mt-3 text-white shrink-0" :class="reserveBadgeRow ? '!mb-1' : '!mb-3'">
      {{ props.plan?.info?.name ? props.plan?.info?.name :
      props.plan?.name }} 
      <Badge v-if="isEventsPlan && !reserveBadgeRow" pill inline type="purple-dark">OTT Apps</Badge>
      <Badge v-if="props.plan?.info?.popular && isCart" type="blue" pill inline
      title="POPULAR" />
    </h2>
    <!-- Same height in every column so prices and actions line up across plans. -->
    <div v-if="reserveBadgeRow" class="h-5 mb-2 flex items-center shrink-0" data-testid="plan-badge-row">
      <Badge v-if="isEventsPlan" pill inline type="purple-dark">OTT Apps</Badge>
    </div>
    <p v-if="props.plan.baseCharge && productType !== 'ott'" class="plan-price flex items-end gap-x-1 my-2 min-w-0 shrink-0">
      <span class="text-2xl font-semibold leading-none text-white whitespace-nowrap">
        ${{ price ? price : props.plan?.baseCharge }}
      </span>
      <span class="text-sm leading-4 text-surface-7 whitespace-nowrap">
        /month
      </span>
    </p>
    <p v-else-if="props.plan?.name === 'Custom' && productType !== 'ott'" class="my-2 text-2xl font-semibold leading-none text-white">
      On Request
    </p>
    <div v-if="props.plan.baseCharge && productType !== 'ott'" class="min-h-[2.25rem] shrink-0">
      <template v-if="props.isYearly">
        <p class="text-tiny text-surface-7">Billed ${{ props.plan.baseCharge }}/year</p>
        <p class="text-tiny font-semibold text-gold">Save ${{ props.plan.info?.saved }}</p>
      </template>
    </div>
    <div v-if="!isCart" class="mt-auto flex flex-col gap-1.5 pt-4 shrink-0">
      <FormButton v-if="props.plan?.name === 'Custom' && productType !== 'ott'"
        class="justify-center"
        :customClasses="[isDisabled ? '!border-surface-6 text-surface-6 hover:text-surface-6 pointer-events-none' : '!border-main text-main hover:!border-main-light hover:text-main-light', 'h-9 flex items-center justify-center rounded-md text-tiny !border hover:no-underline']"
        :disabled="isDisabled" full-width type='main-outline' :label="labelComputed"
        @click="openCalendly()" />
      <FormButton v-else-if="productType !== 'ott'" :disabled="isDisabled" full-width
        :type="props.plan?.info?.popular ? 'main' : 'secondary'"
        class="justify-center shrink-0" :label="labelComputed"
        @click="selectSubscriptionPackage(props.plan)" />
      <FormButton v-else full-width class="justify-center shrink-0"
        label="Contact Us" @click="openCalendly()" />
      <div class="h-5 flex items-center justify-center">
        <FormButton v-if="shouldShowBookMeetingButton(props.plan)" @click="openCalendly()" type="link"
          class="!text-main hover:!text-main-light text-center w-full justify-center" label="Book a Demo" />
      </div>
    </div>
  </header>
</template>

<style scoped>
/*
 * Narrow plan columns — the xl compare grid gives each plan 113px of content
 * at 1280, 125px at 1366 and 130px at 1400 (the query measures the content
 * box; a 320px phone gives the mobile table's plan column 130px too). The
 * inline "$624.99 /month" is up to 147px in the system fonts (Inter: ~130px)
 * and may run into the 16px right padding, so it needs ~131px of content
 * before it is cut off; 1440 has 136px, 1536 (2xl, w-10/12) 134px. Every
 * column in a row has the same width, so keying on the header's own width
 * keeps all columns in the same state (a flex-wrap would stack the unit in
 * some columns and not others), and leaves 1440+ untouched. Tailwind 3.4
 * ships no container-query variants, hence plain CSS.
 */
@container (max-width: 8.25rem) {
  .plan-price {
    flex-direction: column;
    align-items: flex-start;
  }
  /* "Branded Membership Site" is 146px at 12px: one line at 11px/tight, and an
     ellipsis rather than a second line over the plan title if a fallback font
     runs wider. */
  .plan-ribbon {
    font-size: 11px;
    letter-spacing: -0.025em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
